import React from 'react'
import { Link, usePage } from '@inertiajs/react'
import {
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
} from '@mui/material'
import {
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
} from '@mui/icons-material'
import ManagementLayout from '@/components/layouts/ManagementLayout'
import EmptyState from '@/components/shared/EmptyState'
import { PageHeader, CodeBlock } from '@/components/styled'
import type { Credential, Account } from '@/types'

interface Props {
  credentials: Credential[]
}

const permissionLabels: Record<string, string> = {
  read_write: 'Read & Write',
  read_only: 'Read Only',
  write_only: 'Write Only',
}

const permissionColors: Record<string, 'primary' | 'success' | 'warning'> = {
  read_write: 'primary',
  read_only: 'success',
  write_only: 'warning',
}

export default function CredentialsIndex({ credentials }: Props) {
  const { currentAccount, isAccountAdmin } = usePage<{
    currentAccount: Account
    isAccountAdmin: boolean
  }>().props

  return (
    <ManagementLayout>
      <PageHeader
        title="Credentials"
        description="API credentials for accessing your S3 proxy."
        action={
          isAccountAdmin && (
            <Button
              component={Link}
              href={`/accounts/${currentAccount.slug}/credentials/new`}
              variant="contained"
              startIcon={<AddIcon />}
            >
              Create Credential
            </Button>
          )
        }
      />

      {credentials.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Access Key ID</TableCell>
                <TableCell>Permissions</TableCell>
                <TableCell>Virtual Buckets</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {credentials.map((credential) => (
                <TableRow key={credential.id} hover>
                  <TableCell>
                    <Link href={`/accounts/${currentAccount.slug}/credentials/${credential.id}`}>
                      {credential.name}
                    </Link>
                    {credential.description && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {credential.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <CodeBlock>{credential.access_key_id}</CodeBlock>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={permissionLabels[credential.permissions] || credential.permissions}
                      size="small"
                      color={permissionColors[credential.permissions] || 'default'}
                    />
                  </TableCell>
                  <TableCell>{credential.virtualBuckets?.length ?? 0}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      component={Link}
                      href={`/accounts/${currentAccount.slug}/credentials/${credential.id}`}
                      size="small"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    {isAccountAdmin && (
                      <IconButton
                        component={Link}
                        href={`/accounts/${currentAccount.slug}/credentials/${credential.id}/edit`}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <EmptyState
          icon="key"
          title="No credentials"
          description="Create your first API credential."
          actionPath={isAccountAdmin ? `/accounts/${currentAccount.slug}/credentials/new` : undefined}
          actionText={isAccountAdmin ? 'Create Credential' : undefined}
        />
      )}
    </ManagementLayout>
  )
}
